import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useResource } from 'src/hooks/useResource'
import { BackLink } from 'src/components/common'
import {
  getResourceRequest,
  getResourceQuestionsRequest,
} from 'src/services/api.service'
import { useNotification } from 'src/contexts/notification.context'
import { answerStr, pageStr, problemStr } from 'src/tools'
import ViewerTypeSwitcher from 'src/components/builder/viewer-type-switcher'
import { routes } from 'src/navigation/routes'

export const TeacherPracticeQuestions = () => {
  const { id } = useParams()
  const { wrapCallback } = useNotification()
  const { resourceType } = useResource()
  const [practice, setPractice] = useState({})
  const [isShowSolution] = useState(false)

  const [questions, setQuestions] = useState([])
  const [question, setQuestion] = useState(null)
  const [selQuestion, setSelQuestion] = useState(0)

  const [isShowPreview, setIsShowPreview] = useState(false)

  const fetchData = useCallback(async () => {
    wrapCallback(async () => {
      let response = await getResourceRequest(resourceType, id)
      const prItems = response.data?.item

      response = await getResourceQuestionsRequest(resourceType, id)
      const qsItems = response.data?.items

      const answer_type = [
        ...new Set(qsItems.map((x) => answerStr(x.answer_type))),
      ]
        .filter((x) => x)
        .join(', ')
      const problem_type = [
        ...new Set(qsItems.map((x) => problemStr(x.problem_type))),
      ]
        .filter((x) => x)
        .join(', ')

      setPractice({ ...prItems, answer_type, problem_type })
      setQuestions(qsItems)
    })
  }, [])

  useEffect(() => {
    fetchData()
  }, [])

  const handleSelectQuestion = (idx) => () => {
    setSelQuestion(idx)
  }

  useEffect(() => {
    const q = selQuestion < questions.length ? questions[selQuestion] : null

    if (q) {
      setQuestion({
        ...q,
      })
    }
  }, [selQuestion, questions])

  const handleNextQuestion = (e) => {
    e.preventDefault()

    if (selQuestion < questions.length - 1) {
      setSelQuestion(selQuestion + 1)
    }
  }

  const handlePrevQuestion = (e) => {
    e.preventDefault()
    if (selQuestion > 0) {
      setSelQuestion(selQuestion - 1)
    }
  }

  const handleTogglePreview = () => {
    setIsShowPreview(!isShowPreview)
  }

  if (!questions || !question || !practice) {
    return null
  }

  return (
    <>
      <div className="sectionHb-top sectionHb-top__space">
        <div className="sectionHb-top__row">
          <BackLink to={routes[resourceType].index} />
          <h2 className="sectionHb-title">Review</h2>
          <Link
            to={routes[resourceType].edit(practice.id)}
            className="review-link"
          >
            <i className="hb-ico pencil-ico" />
            <span>Edit</span>
          </Link>
        </div>

        {Number(practice.time) > 0 && (
          <div className="sectionHb-timer">
            Timer: <b>{practice.time}</b>minutes
          </div>
        )}
      </div>

      <div className="sectionHb-block review">
        <div className="sectionHb-block__wrap">
          <h3 className="review-title">
            {practice?.subject?.name} {practice?.grade?.name} grade
          </h3>
          <div className="review-edit">
            <ul>
              {practice?.levels?.map((x) => (
                <li key={x.id}>
                  <span>{x.name}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="review-table review-table__second">
            <div className="review-table__row">
              <div className="review-table__col review-table__col-1">
                Title: &nbsp; <b>{practice.title}</b>
              </div>
            </div>
            <div className="review-table__row">
              <div className="review-table__col review-table__col-1">
                <span>Problem type:</span>
                <b>{practice.problem_type}</b>
              </div>
              <div className="review-table__col review-table__col-2">
                <span>Total problems:</span>
                <b>{questions.length}</b>
              </div>
            </div>
            <div className="review-table__row">
              <div className="review-table__col review-table__col-1">
                <span>Answer type:</span>
                <b>{practice.answer_type}</b>
              </div>
              <div className="review-table__col review-table__col-2">
                <span>Page layout:</span>
                <b>{pageStr(practice.page_layout)}</b>
              </div>
            </div>
          </div>

          <div style={{ display: isShowPreview ? 'block' : 'none' }}>
            <div className="problem-wrap-view">
              <div className="progress progress-static">
                {new Array(questions.length).fill(1).map((_, i) =>
                  selQuestion === i ? (
                    <div
                      key={i}
                      onClick={handleSelectQuestion(i)}
                      className="progress-col current"
                    >
                      {i + 1}/{questions.length}
                    </div>
                  ) : (
                    <div
                      key={i}
                      onClick={handleSelectQuestion(i)}
                      className={`progress-col ${
                        selQuestion > i ? 'active' : ''
                      }`}
                    />
                  ),
                )}
              </div>

              {question && (
                <>
                  <ViewerTypeSwitcher
                    {...question}
                    id={question?.id}
                    playMode={false}
                    reviewMode={false}
                  />

                  {(question?.hint || question.hint_picture) && (
                    <>
                      <div className="cabinet__intro__title">Hint</div>
                      <div className="stats">
                        <div className="stats__inner">
                          <h3 className="stats__title">{question?.hint}</h3>

                          {question?.hint_picture && (
                            <div className="stats__wrapper">
                              <img src={question?.solution_picture} />
                            </div>
                          )}
                        </div>
                      </div>
                      <br />
                    </>
                  )}

                  {isShowSolution && (
                    <>
                      <div className="cabinet__intro__title">Solution</div>
                      <div className="stats">
                        <div className="stats__inner">
                          <h3 className="stats__title">{question?.solution}</h3>
                          {question?.solution_picture && (
                            <div className="stats__wrapper">
                              <img src={question?.solution_picture} />
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <div className="buttons-row buttons-row__big">
              <a
                href="#"
                className={`second-btn second-btn__prev ${
                  selQuestion === 0 ? 'a-disabled' : ''
                }`}
                onClick={handlePrevQuestion}
              >
                <i className="hb-ico second-arrow" />
                Previous {}
              </a>
              {/* <a href="#" className="primary-btn" onClick={(e) => { e.preventDefault(); setIsShowSolution(!isShowSolution) }}>
                {isShowSolution ? 'Hide Solution' : 'Show Solution'}
              </a> */}
              <Link
                to={routes[resourceType].questions.edit(
                  practice.id,
                  question.id,
                )}
                className="primary-btn"
              >
                Edit
              </Link>
              <a
                href="#"
                className={`second-btn second-btn__next ${
                  selQuestion === questions.length - 1 ? 'a-disabled' : ''
                }`}
                onClick={handleNextQuestion}
              >
                Next
                <i className="hb-ico second-arrow-2" />
              </a>
            </div>
          </div>

          {practice.price > 0 && (
            <div className="review-price">{practice.price}</div>
          )}

          {/* <div className="buttons-row buttons-row__big">
            <a href="#" className="primary-btn">Save to library</a>
            <a href="#" className="second-btn">
              Report
            </a>
            <a href="#" className="second-btn second-btn__like">
              <i className="hb-ico like-ico"></i>
            </a>
          </div> */}

          <div className="review-checkbox">
            <div className="checkbox">
              <input type="checkbox" />
              <i className="checkbox-ico" />
              <p>Publish to Practithink marketplace</p>
            </div>
          </div>

          <div className="buttons-row">
            <Link to={routes[resourceType].index} className="primary-btn">
              Save
            </Link>
            <button
              onClick={handleTogglePreview}
              type="button"
              className="second-btn"
            >
              Preview/Edit
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
